import { Chip } from "@mui/material";
import { capitalize } from "lodash";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../components/CustomDataGridV2";
import Flex from "../../../../../../../components/Flex";
import OutlineCardV2 from "../../../../../../../components/OutlineCardV2";
import RenderWithTooltip from "../../../../../../../components/RenderWithTooltip";
import { ScrollArea } from "../../../../../../../components/ScrollBar";
import {
  CHART_COLORS,
  DataFieldRow,
} from "../../../../../../../components/app/provider/patient/chart";
import {
  ALIAS,
  PRIMITIVE,
  px,
} from "../../../../../../../tokens/design-tokens";
import { GENDER_RECORD } from "../../../../../../../constants";
import { EMERGENCY_CONTACT_RELATION_MAPPER } from "../../../../../../../constants/provider/add-new-patient";
import {
  ENTHNICITY_MAPPER,
  RACE_MAPPER,
} from "../../../../../../../dummy-data/add-new-patient";
import { formatDate } from "../../../../../../../utils/date-time-utility";
import {
  // DEFAULT_FONTSIZE,
  // DEFAULT_LABEL_COLOR_2,
  DEFAULT_LABEL_COLOR_RED,
  applyDefaultColumnSettings,
  getAddressFormat,
  getValueFromObj,
} from "../../../../../../../utils/utils";
import TitleStripV2 from "./DemographicsTitleStripV2";
import { DEMO_TABLE_SX } from "./demographics-contant";

const DemographicsNew = () => {
  const { patientData: profileData }: any = useOutletContext();
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "relationshipWithPatient",
      flex: 0.6,
      headerName: "Patient Relationship",
      minWidth: 190,
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row?.relationshipWithPatient
          ? EMERGENCY_CONTACT_RELATION_MAPPER[row?.relationshipWithPatient]
          : "-",
    },
    {
      field: "name",
      flex: 0.6,
      headerName: "Name",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ row }) =>
        row?.firstName || row?.lastName
          ? ` ${row?.firstName || ""} ${row?.lastName || ""}`
          : "-",
    },
    {
      field: "mobile",
      flex: 0.6,
      headerName: "Contact No.",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "email",
      flex: 0.6,
      headerName: "Email",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
  ]);

  const PREFERENCES_COLUMN = applyDefaultColumnSettings<any>([
    {
      field: "preferenceType",
      headerName: "Type",
      renderCell: ({ value }) => (value ? capitalize(value) : "-"),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 0.5,
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 0.5,
      renderCell: ({ value }) => (
        <RenderWithTooltip value={getAddressFormat(value) || "-"} />
      ),
    },
    {
      field: "faxNumber",
      headerName: "Fax No.",
      flex: 0.5,
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "contactNumber",
      flex: 0.6,
      headerName: "Contact No.",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
    },
  ]);

  return (
    <ScrollArea
      height={isVisitNotePage ? "100%" : "calc(100vh - 300px)"}
      width={"100%"}
    >
      <Flex
        height={"inherit"}
        width={"100%"}
        gap={1.5}
        p={1.5}
        flexDirection={isVisitNotePage ? "column" : "row"}
      >
        {/* Left Column: Basic Info, Registering Info, Privacy Consent, Patient Status */}
        <Flex
          flexDirection={"column"}
          gap={1.5}
          width={isVisitNotePage ? "100%" : "43%"}
        >
          <OutlineCardV2 sx={{ padding: 0 }}>
            <TitleStripV2
              title="Basic Information"
              preSelectedTab="Patient Details"
            />
            {[
              [
                { label: "Contact No.", value: profileData?.mobileNumber },
                {
                  label: "Language",
                  value: profileData?.languages
                    ? Object.values(profileData?.languages).join(", ")
                    : "",
                },
              ],
              [
                { label: "Email", value: profileData?.email },
                { label: "Race", value: RACE_MAPPER[profileData?.race] },
              ],
              [
                { label: "SSN", value: profileData?.ssn },
                {
                  label: "Ethnicity",
                  value: ENTHNICITY_MAPPER[profileData?.ethnicity],
                },
              ],
              [
                {
                  label: "Marital Status",
                  value: profileData?.maritalStatus
                    ? capitalize(profileData?.maritalStatus)
                    : "",
                },
                {
                  label: "Gender",
                  value: GENDER_RECORD[profileData?.gender],
                },
              ],
              [
                {
                  label: "Address",
                  value: getAddressFormat(profileData?.address),
                  stretch: true,
                },
              ],
            ].map((row, idx) => (
              <Flex
                key={idx}
                gap={1.5}
                sx={{
                  backgroundColor:
                    idx % 2 === 0
                      ? CHART_COLORS.surfaceWhite
                      : CHART_COLORS.surfaceAlt,
                  // borderLeft: `1px solid ${CHART_COLORS.border}`,
                  // borderRight: `1px solid ${CHART_COLORS.border}`,
                  ...(idx === 4 && {
                    // borderBottom: `1px solid ${CHART_COLORS.border}`,
                    borderRadius: "0 0 4px 4px",
                  }),
                  px: 1.5,
                  py: 0.5,
                }}
              >
                {row.map((field) => (
                  <DataFieldRow
                    key={field.label}
                    label={field.label}
                    value={field.value || "-"}
                    stretchValue={(field as any).stretch}
                  />
                ))}
              </Flex>
            ))}
          </OutlineCardV2>

          {/* Registering Information */}
          <OutlineCardV2 style={{ padding: 0 }}>
            <TitleStripV2
              title="Registering Information"
              preSelectedTab="Provider Information"
            />
            <Flex
              gap={1.5}
              sx={{
                backgroundColor: CHART_COLORS.surfaceWhite,
                // bgcolor:"red",
                borderRadius: "0 0 4px 4px",
                px: 1.5,
                py: 0.5,
              }}
            >
              <DataFieldRow
                label="Primary Provider"
                value={getValueFromObj(profileData?.providerId) || "-"}
              />
              <DataFieldRow
                label="Registration Date"
                value={formatDate(profileData?.registrationDate) || "-"}
              />
            </Flex>
          </OutlineCardV2>

          {/* Privacy Consent */}
          <OutlineCardV2
            style={{ padding: 0, boxSizing: "border-box", overflow: "hidden" }}
          >
            <TitleStripV2 title="Privacy Consent" preSelectedTab="Consent" />
            {/* <Flex
              sx={{
                backgroundColor: CHART_COLORS.surfaceWhite,
                borderLeft: `1px solid ${CHART_COLORS.border}`,
                borderRight: `1px solid ${CHART_COLORS.border}`,
                px: 1.5,
                py: 0.5,
              }}
            >
              <DataFieldRow
                label="Default Channel"
                value={profileData?.defaultCommunicationChannel || "-"}
                stretchValue
              />
            </Flex> */}
            <Flex
              flexWrap="wrap"
              gap={1.5}
              sx={{
                backgroundColor: CHART_COLORS.surfaceAlt,
                borderRadius: "0 0 4px 4px",
                px: 1.5,
                py: 0.5,
              }}
            >
              <DataFieldRow
                label="Email"
                value={profileData?.emailConsent === true ? "Yes" : "No"}
              />
              <DataFieldRow
                label="Call"
                value={profileData?.callConsent === true ? "Yes" : "No"}
              />
              <DataFieldRow
                label="Message"
                value={profileData?.messageConsent === true ? "Yes" : "No"}
              />
            </Flex>
          </OutlineCardV2>

          {/* Patient Status */}
          <OutlineCardV2 style={{ padding: 0, overflow: "auto" }}>
            <TitleStripV2 title="Privacy Status" showEditButton={false} />
            <Flex
              gap={1.5}
              sx={{
                backgroundColor: CHART_COLORS.surfaceWhite,
                borderRadius: "0 0 4px 4px",
                px: 1.5,
                py: 0.5,
              }}
            >
              <DataFieldRow
                label="Status"
                value={
                  <StatusMapper
                    status={profileData?.active ? "Active" : "Inactive"}
                  />
                }
              />
            </Flex>
          </OutlineCardV2>
        </Flex>

        {/* Right Column: Preferences, Emergency Contact */}
        <Flex
          flexDirection={"column"}
          gap={1.5}
          flex={isVisitNotePage ? undefined : 1}
          width={isVisitNotePage ? "100%" : undefined}
          minWidth={0}
        >
          {/* Preferences */}
          <OutlineCardV2
            style={{ padding: 0, display: "flex", flexDirection: "column" }}
          >
            <TitleStripV2 title="Preferences" preSelectedTab="Preferences" />
            <Flex
              // flex={1}
              flexDirection="column"
              sx={DEMO_TABLE_SX}
            >
              <CustomDataGridV2
                style={{ maxHeight: 200, minHeight: 100 }}
                rowHeight={28}
                hideFooter
                getRowId={(row) => row.uuid}
                rows={profileData?.pharmacyLabRadiologyMap ?? []}
                columns={PREFERENCES_COLUMN}
                disableRowSelectionOnClick
              />
            </Flex>
          </OutlineCardV2>

          {/* Emergency Contact */}
          <OutlineCardV2
            style={{
              padding: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <TitleStripV2
              title="Emergency Contact"
              preSelectedTab="Emergency Contact"
            />
            <Flex
              // flex={1}
              flexDirection="column"
              sx={DEMO_TABLE_SX}
            >
              <CustomDataGridV2
                style={{ maxHeight: 200, minHeight: 0 }}
                rowHeight={28}
                hideFooter
                getRowId={(row) => row.uuid}
                rows={profileData?.emergencyContacts ?? []}
                columns={COLUMNS}
                disableRowSelectionOnClick
              />
            </Flex>
          </OutlineCardV2>
        </Flex>
      </Flex>
    </ScrollArea>
  );
};

export default DemographicsNew;

const StatusMapper = ({ status }: { status: string }) => {
  const isActive = status === "Active";
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        height: "22px",
        fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
        fontWeight: PRIMITIVE.typography.fontWeight.medium,
        backgroundColor: isActive
          ? ALIAS.semantics.success.bg
          : ALIAS.semantics.error.bg,
        color: isActive
          ? ALIAS.semantics.success.pressed
          : DEFAULT_LABEL_COLOR_RED,
      }}
    />
  );
};
